/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import type { LocalePrefixMode } from "../types/locale.types";

/**
 * Normalizes a path to ensure it starts with a forward slash
 *
 * @param path - The path to normalize
 * @returns The path with a leading slash
 *
 * @example
 * normalizePath("about") // => "/about"
 * normalizePath("/about") // => "/about"
 */
export function normalizePath(path: string): string {
	return path.startsWith("/") ? path : `/${path}`;
}

/**
 * Generates a locale-aware path based on prefix mode
 *
 * @param path - The path without locale prefix (e.g., "/about")
 * @param locale - Current locale (e.g., "de")
 * @param defaultLocale - Default locale (e.g., "en-US")
 * @param localePrefix - Prefix mode configuration (defaults to "always")
 * @returns The path with or without locale prefix based on configuration
 *
 * @example
 * // With "always" mode (default)
 * getLocalizedPath("/about", "de", "en-US", "always") // => "/de/about"
 * getLocalizedPath("/about", "en-US", "en-US", "always") // => "/en-US/about"
 *
 * @example
 * // With "as-needed" mode
 * getLocalizedPath("/about", "de", "en-US", "as-needed") // => "/de/about"
 * getLocalizedPath("/about", "en-US", "en-US", "as-needed") // => "/about"
 *
 * @example
 * // With "never" mode
 * getLocalizedPath("/about", "de", "en-US", "never") // => "/about"
 *
 * @example
 * // In navigation components
 * const GlobalNav = () => {
 *   const { getPath } = useLocalePrefix();
 *   return <Link href={getPath("/about", locale)}>About</Link>;
 * };
 *
 * @example
 * // In sitemap generation
 * const sitemapUrl = getLocalizedPath(slug, locale, defaultLocale, "as-needed");
 */
export function getLocalizedPath(
	path: string,
	locale: string,
	defaultLocale: string,
	localePrefix: LocalePrefixMode = "always",
): string {
	const normalizedPath = normalizePath(path);

	if (localePrefix === "never") {
		return normalizedPath;
	}

	if (localePrefix === "as-needed" && locale === defaultLocale) {
		return normalizedPath;
	}

	return `/${locale}${normalizedPath}`;
}

/**
 * Removes locale prefix from path if present
 *
 * @param path - The path that may contain a locale prefix
 * @param locales - Array of supported locale codes
 * @returns The path without locale prefix
 *
 * @example
 * removeLocaleFromPath("/en-US/about", ["en-US", "de"]) // => "/about"
 * removeLocaleFromPath("/about", ["en-US", "de"]) // => "/about"
 */
export function removeLocaleFromPath(path: string, locales: string[]): string {
	if (!path || !locales?.length) return path || "/";

	const normalizedPath = normalizePath(path);

	for (const locale of locales) {
		if (normalizedPath.startsWith(`/${locale}/`)) {
			return normalizedPath.slice(locale.length + 1);
		}
		if (normalizedPath === `/${locale}`) {
			return "/";
		}
	}

	return normalizedPath;
}

/**
 * Checks if the given locale should have a URL prefix based on the prefix mode
 *
 * @param locale - The locale to check
 * @param defaultLocale - The default locale
 * @param localePrefix - The prefix mode
 * @returns Whether the locale should have a URL prefix
 */
export function shouldHaveLocalePrefix(
	locale: string,
	defaultLocale: string,
	localePrefix: LocalePrefixMode = "always",
): boolean {
	if (localePrefix === "never") {
		return false;
	}

	if (localePrefix === "as-needed") {
		return locale !== defaultLocale;
	}

	return true;
}
